import { useTranslation } from "react-i18next";
import { InnoveraLoading, FigmaTypography } from "@libs/zapi-ui/custom";
import { chapterIcons, defaultChapterIcon } from "@libs/zapi-ui/const";
import { cn } from "@/zapi-frontend/shared/lib/utils";
import { useProjectStore } from "@/zapi-frontend/stores/project";
import { useChapterStore } from "@/zapi-frontend/stores/chapter";
import { Check } from "@phosphor-icons/react";
import type { TChapterStatusMap } from "@/zapi-frontend/shared/hooks/useProjectSetupPolling";
import type { TChapterMemoStatus } from "@/zapi-frontend/shared/hooks/useChapterMemoPolling";
import { useSearchParams } from "react-router-dom";
import { DashboardProgressIcon } from "./_DashboardProgressIcon";
import { useMeStore } from "@/zapi-frontend/shared/hooks/useMe";
import { CancelGenerationButton } from "../CancelGenerationButton";
import { isAppendixChapter } from "../ProjectSidebar/ProjectSidebar";

type DashboardIslandProps = {
  chapterStatusMap: TChapterStatusMap | null;
  chapterMemoPolling: {
    chapters: TChapterMemoStatus[];
    isPolling: boolean;
  };
  onCancelSetup?: () => void;
  isCancelling?: boolean;
};

export function DashboardIsland({
  chapterStatusMap,
  chapterMemoPolling,
  onCancelSetup,
  isCancelling = false
}: DashboardIslandProps) {
  const { selectedProject } = useProjectStore();
  const { projectChapters, setActiveChapter } = useChapterStore();
  const { isLabs } = useMeStore();
  const [, setSearchParams] = useSearchParams();
  const { t } = useTranslation(["projectHome", "common"]);

  const handleChapterClick = (chapterId: string) => {
    setActiveChapter(chapterId);
    setSearchParams((prev) => {
      const params = new URLSearchParams(prev);
      params.set("chapterId", chapterId);
      params.delete("chapterSubsection");
      return params;
    });
  };

  if (!selectedProject) {
    return (
      <div className={cn("w-full h-full flex flex-col items-center justify-center gap-2")}>
        <InnoveraLoading size={80} />
        <FigmaTypography variant="body-2" color="zinc500">
          {t("common:loadingProject")}
        </FigmaTypography>
      </div>
    );
  }

  const getProgressPercentage = (chapter: (typeof projectChapters)[0]) => {
    if (chapter.totalQuestions === 0) return 0;
    return (chapter.validQuestionsCount / chapter.totalQuestions) * 100;
  };

  const getProjectInitial = (name: string) => {
    return name.charAt(0).toUpperCase();
  };

  const isDocumentParsingPhase = projectChapters.some((chapter) => {
    const status = chapterStatusMap?.[chapter.id.toString()];
    return status?.phase === "documentParsing";
  });

  return (
    <div className={cn("w-full h-full flex items-start justify-center p-4 pt-0 overflow-y-auto")}>
      <div className="flex flex-col w-[720px] pb-8">
        <div className="flex-shrink-0 w-full bg-white rounded-[10px]">
          <div className="flex items-center gap-3 pb-[20px] pt-[30px]">
            <div className="flex-shrink-0">
              <div className="size-[44px] bg-[#a553fd] rounded-[5px] flex items-center justify-center p-2">
                <FigmaTypography variant="heading-1" className="text-white">
                  {getProjectInitial(selectedProject.name)}
                </FigmaTypography>
              </div>
            </div>
            <div className="flex-1 min-w-0 flex flex-col items-start justify-center">
              <FigmaTypography variant="heading-2" className="truncate max-w-full">
                {selectedProject.name}
              </FigmaTypography>
              <FigmaTypography variant="body-2" className="truncate max-w-full">
                {selectedProject.shortDescription}
              </FigmaTypography>
            </div>
            {onCancelSetup && (
              <div className="shrink-0">
                <CancelGenerationButton onCancel={onCancelSetup} isCancelling={isCancelling} />
              </div>
            )}
          </div>
        </div>

        <div
          className={cn(
            "flex-shrink-0 w-full bg-white rounded-[10px] border border-innoveraZinc-150 relative",
            isDocumentParsingPhase && "min-h-[614px]"
          )}
        >
          <div className="border-b border-innoveraZinc-150">
            <div className="flex items-center h-[44px] px-5">
              <div className="flex items-center h-5 w-[251px]">
                <FigmaTypography variant="label-3" color="zinc500">
                  {t("projectHome:dashboard.chapter")}
                </FigmaTypography>
              </div>
              {!isDocumentParsingPhase && (
                <div className="flex items-center h-5 flex-1">
                  <FigmaTypography variant="label-3" color="zinc500">
                    {t("projectHome:dashboard.taskInProgress")}
                  </FigmaTypography>
                </div>
              )}
            </div>
            {isDocumentParsingPhase && (
              <div className="absolute right-[16px] top-[16px] w-[360px] h-[582px] bg-white/80 backdrop-blur-md border border-innoveraZinc-150 rounded-[10px] p-5 flex flex-col items-center justify-center">
                <InnoveraLoading size={80} />
                <FigmaTypography variant="label-1" className="mt-6 mb-2 text-center">
                  {t("projectHome:dashboard.documentsBeingAnalyzed")}
                </FigmaTypography>
                <FigmaTypography variant="body-2" color="zinc500" className="text-center">
                  {t("projectHome:dashboard.analyzeTimeEstimate")}
                </FigmaTypography>
              </div>
            )}
          </div>

          <div className="pb-5 pt-0 px-5">
            {projectChapters
              .filter((chapter) => (!chapter.isHidden || isLabs) && !isAppendixChapter(chapter))
              .map((chapter) => {
                const progressPercent = getProgressPercentage(chapter);
                const chapterMemoStatus = chapterMemoPolling.chapters.find((c) => c.id === chapter.id);
                const hasActiveMemo = !!chapterMemoStatus?.hasActiveMemo;
                const isStatusLoaded = chapterStatusMap !== null;
                const isComplete = hasActiveMemo && isStatusLoaded;
                const IconComponent = chapter.projectTemplateChapter.icon
                  ? chapterIcons[chapter.projectTemplateChapter.icon]
                  : chapterIcons[defaultChapterIcon];

                const chapterStatus = chapterStatusMap?.[chapter.id.toString()];
                const statusMessage = chapterStatus?.message || chapter.projectTemplateChapter.name;

                return (
                  <div
                    key={chapter.id}
                    className="flex items-center gap-2.5 h-[50px] border-b border-innoveraZinc-150 last:border-b-0"
                  >
                    <div className="flex items-center gap-3 h-5 w-[251px]">
                      <div className="flex items-center justify-center w-8 shrink-0">
                        <div className="relative size-8">
                          <DashboardProgressIcon isComplete={isComplete} progressPercent={progressPercent} />
                          <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 flex items-center justify-center">
                            <IconComponent className="size-4" color="var(--innovera-zinc900)" weight="regular" />
                          </div>
                        </div>
                      </div>
                      <FigmaTypography variant="label-1">{chapter.projectTemplateChapter.name}</FigmaTypography>
                    </div>

                    {!isDocumentParsingPhase && (
                      <div className="flex items-center gap-3 h-5 flex-1">
                        {isComplete ? (
                          <>
                            <Check className="size-5 shrink-0" color="var(--innovera-green500)" weight="bold" />
                            <FigmaTypography variant="body-2" color="zinc600">
                              {t("projectHome:dashboard.complete")}
                            </FigmaTypography>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleChapterClick(chapter.id.toString());
                              }}
                              className="ml-auto px-3 py-1 rounded-md text-[14px] leading-[22px] bg-innoveraZinc-100 text-innoveraZinc-900 hover:bg-innoveraZinc-150 transition-colors"
                            >
                              {t("projectHome:dashboard.view")}
                            </button>
                          </>
                        ) : (
                          <>
                            <InnoveraLoading size={20} />
                            <FigmaTypography variant="body-2" color="zinc600">
                              {statusMessage}
                            </FigmaTypography>
                          </>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
          </div>
        </div>
      </div>
    </div>
  );
}
